// ナンバープレース**固有**の設定（localStorage 永続）。
//
// ポータル横断の設定（音量・ミュート・BGM・言語）は
// [platform/net/settings.ts](@/platform/net/settings) が持つ。ここにあるのは
// 「このゲームでしか意味を持たない」項目だけ。所属の決め方は CLAUDE.md の
// 「設定は所属で2つに分かれる」を参照。
//
// キーは `nanpure.*`（＝`game_key` と同じ文字列）。データ削除が接頭辞で掃くので、
// ここに増やしたぶんは自動で消える対象になる。
import type { GameSettingsSlice } from '@/platform/net/settings';
import { GAME_KEY } from '../gameKey';

const K_HIGHLIGHT = `${GAME_KEY}.highlightOn`;
const K_SAME_DIGIT = `${GAME_KEY}.sameDigitOn`;

export const GAME_SETTINGS_DEFAULTS = {
  /** 選んだマスの行・列・ブロックをうっすら示す。 */
  highlightOn: true,
  /** 選んだマスと同じ数字のマスを示す。 */
  sameDigitOn: true,
} as const;

/**
 * 行・列・ブロックの強調（既定 ON）。
 *
 * 🔴 **既定を ON にするのは親切のためではない**。9×9 のマスは 76px で、紙の上に指を置くと
 *   **自分の指で行と列が隠れる**。⚠️ 見えていない列に同じ数字を置くとその場でミス＝
 *   ハートが1つ減る（仕様7）のに、本人は「確かめたつもり」なので原因が分からない。
 *   ⇒ 消せるようにはするが、**既定では出す**。
 */
export function isHighlightOn(): boolean {
  const v = localStorage.getItem(K_HIGHLIGHT);
  return v === null ? GAME_SETTINGS_DEFAULTS.highlightOn : v === '1';
}
export function setHighlightOn(on: boolean): void {
  localStorage.setItem(K_HIGHLIGHT, on ? '1' : '0');
}

/**
 * 同じ数字の強調（既定 ON）。
 * ⚠️ 行・列・ブロックの強調とは**別の設定**にする。片方だけ切りたい人がいるし、
 *   まとめて1つにすると「どちらの色が何を意味するのか」を説明できなくなる。
 */
export function isSameDigitOn(): boolean {
  const v = localStorage.getItem(K_SAME_DIGIT);
  return v === null ? GAME_SETTINGS_DEFAULTS.sameDigitOn : v === '1';
}
export function setSameDigitOn(on: boolean): void {
  localStorage.setItem(K_SAME_DIGIT, on ? '1' : '0');
}

/** このゲームの設定を既定値へ戻す。 */
export function resetGameSettings(): void {
  setHighlightOn(GAME_SETTINGS_DEFAULTS.highlightOn);
  setSameDigitOn(GAME_SETTINGS_DEFAULTS.sameDigitOn);
}

/**
 * サーバー同期用のスライス。`registry.ts` に載せると
 * `/api/settings` の `games.<gameKey>` として自動で往復するようになる。
 */
export const nanpureSettingsSlice: GameSettingsSlice = {
  gameKey: GAME_KEY,
  read: () => ({
    highlightOn: isHighlightOn(),
    sameDigitOn: isSameDigitOn(),
  }),
  apply: (s) => {
    if (typeof s.highlightOn === 'boolean') setHighlightOn(s.highlightOn);
    if (typeof s.sameDigitOn === 'boolean') setSameDigitOn(s.sameDigitOn);
  },
  reset: resetGameSettings,
};
